import React, {useMemo} from 'react';
import {size, filter, cloneDeep} from 'lodash';
import {useHistory, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {Store, ChecklistItem} from 'src/types/store';

import Checklist from '../checklist';

interface Props {
  onDismiss: () => void;
  navigateToCollegeSearch: () => void;
}

const CCChecklist = ({navigateToCollegeSearch, onDismiss}: Props) => {
  const history = useHistory();
  const location = useLocation();

  const student = useSelector((state: Store) => state.student);
  const collegeCourses = useSelector((state: Store) => state.collegeCourses);
  const activities = useSelector((state: Store) => state.activities);
  const communityServiceEvents = useSelector((state: Store) => state.communityServiceEvents);
  const workExperiences = useSelector((state: Store) => state.workExperiences);
  const referralModal = useSelector((state: Store) => state.referralModal);

  const checklistItems: Record<string, ChecklistItem> = useMemo(() => {
    // Initialize checklist items.
    const BASE_ITEMS = {
      'follow-a-college': {
        title: 'Follow at least 1 college you’re interested in',
        description: 'You can see your potential earnings for followed colleges.',
        allowSkip: false,
        cta: () => navigateToCollegeSearch(),
        ctaLabel: 'Search Colleges',
        isComplete: () => !!student.followedCollegeIds.length,
        order: 1,
      },
      'add-courses': {
        title: 'Add 2 courses ',
        description: 'Add 2 courses and start earning scholarships.',
        allowSkip: false,
        cta: () => history.replace(location.pathname + '/course/new', {skipScrolling: true}),
        ctaLabel: 'Add Courses',
        isComplete: () => filter(collegeCourses, (course) => !course.new).length >= 2,
        order: 2,
      },
      'add-work': {
        title: 'Add 1 Work Experience to your portfolio',
        description: 'Add to your portfolio and earn even more.',
        allowSkip: true,
        cta: () =>
          history.replace(location.pathname + '/work-experience/new', {skipScrolling: true}),
        ctaLabel: 'Add Work Experience',
        isComplete: () => filter(workExperiences, (work) => !work.new).length > 0,
        order: 3,
      },
      'add-extracurricular': {
        title: 'Add 1 Extracurricular Activity to your portfolio',
        description: 'Add to your portfolio and earn even more.',
        allowSkip: true,
        cta: () => history.replace(location.pathname + '/activity/new', {skipScrolling: true}),
        ctaLabel: 'Add Extracurricular',
        isComplete: () => size(activities) > 0,
        order: 4,
      },
      'add-community-service': {
        title: 'Add 1 Community Service to your portfolio',
        description: 'Add to your portfolio and see how much you could earn.',
        allowSkip: true,
        cta: () =>
          history.replace(location.pathname + '/community-service/new', {skipScrolling: true}),
        ctaLabel: 'Add Community Service',
        isComplete: () => size(communityServiceEvents) > 0,
        order: 5,
      },
      referral: {
        title: 'Invite a Friend to Raise Me',
        description: 'Some colleges award a micro-scholarship for this.',
        allowSkip: true,
        ctaLabel: 'Invite',
        cta: () =>
          history.replace(location.pathname + '/refer', {skipScrolling: true, showSkip: true}),
        isComplete: () =>
          !!student.studentInvitations?.length ||
          (referralModal.invitations && referralModal.invitations.length > 0),
        order: 6,
      },
      'add-courses-2': {
        title: 'Add 5 total courses',
        description: 'Add 5 courses total to earn even more.',
        allowSkip: false,
        cta: () => history.replace(location.pathname + '/course/new', {skipScrolling: true}),
        ctaLabel: 'Add Courses',
        isComplete: () => filter(collegeCourses, (course) => !course.new).length >= 5,
        order: 8,
      },
    };

    const items = cloneDeep(BASE_ITEMS);

    return items;
  }, [
    activities,
    collegeCourses,
    communityServiceEvents,
    history,
    location.pathname,
    navigateToCollegeSearch,
    referralModal.invitations,
    student.followedCollegeIds.length,
    student.studentInvitations?.length,
    workExperiences,
  ]);

  return (
    <Checklist
      skippedChecklistItems={student.skippedChecklistItems}
      firstName={student.firstName}
      onDismiss={onDismiss}
      listItems={checklistItems}
      tipContent="
          Keep adding all your community college courses (no matter what grade you earned) to ensure
          that you can redeem your scholarships when you transfer.
        "
      onCourseAdd={() => history.replace(`${location.pathname}/course/new`, {skipScrolling: true})}
    />
  );
};

export default CCChecklist;
